/**
 * CouncilVisualizationWithDemo Component
 *
 * A wrapper that intelligently alternates between the CouncilVisualization
 * (orbiting AI nodes) and a product demo video.
 *
 * Behavior:
 * - Starts with CouncilVisualization (establishes brand identity)
 * - After a delay, crossfades to the demo video
 * - After video plays through, crossfades back to CouncilVisualization
 * - Repeats this cycle while user is on the landing page
 * - If user interacts with research input, stays on CouncilVisualization
 *
 * Design Philosophy:
 * - Smooth crossfade transitions (not jarring cuts)
 * - Video player has premium Mac-style window chrome
 * - Both elements share similar visual weight for balanced swap
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { cn } from '@/lib/utils';
import { CouncilVisualization } from './CouncilVisualization';
import { DemoVideoPlayer } from './DemoVideoPlayer';
import type { ResearchPhase } from '@/hooks/useResearchSession';

interface CouncilVisualizationWithDemoProps {
  className?: string;
  /** Whether a query is active/being processed */
  isActive?: boolean;
  /** Current research phase for enhanced visual feedback */
  phase?: ResearchPhase;
  /** Disable automatic swap (for when user is interacting) */
  disableAutoSwap?: boolean;
}

// Timing constants
const INITIAL_DELAY_MS = 8000; // Show visualization for 8s before first video
const VIDEO_DURATION_MS = 45000; // Rough video duration (will also use onEnded)
const VISUALIZATION_DURATION_MS = 12000; // Show visualization for 12s between videos
const CROSSFADE_DURATION_MS = 800; // Crossfade animation duration

type DisplayMode = 'visualization' | 'video';

export function CouncilVisualizationWithDemo({
  className,
  isActive = false,
  phase,
  disableAutoSwap = false,
}: CouncilVisualizationWithDemoProps) {
  const [currentMode, setCurrentMode] = useState<DisplayMode>('visualization');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const hasShownVideoOnce = useRef(false);

  // Clear any existing timer
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Transition to a new mode with crossfade
  const transitionTo = useCallback((newMode: DisplayMode) => {
    if (disableAutoSwap && newMode === 'video') return;

    setIsTransitioning(true);
    // After half the crossfade, swap the content
    setTimeout(() => {
      setCurrentMode(newMode);
      // Complete the transition
      setTimeout(() => {
        setIsTransitioning(false);
      }, CROSSFADE_DURATION_MS / 2);
    }, CROSSFADE_DURATION_MS / 2);
  }, [disableAutoSwap]);

  // Schedule next transition
  const scheduleNextTransition = useCallback(() => {
    clearTimer();

    if (disableAutoSwap || isActive) {
      // Stay on visualization when user is interacting
      if (currentMode === 'video') {
        transitionTo('visualization');
      }
      return;
    }

    if (currentMode === 'visualization') {
      // Schedule transition to video
      const delay = hasShownVideoOnce.current ? VISUALIZATION_DURATION_MS : INITIAL_DELAY_MS;
      timerRef.current = setTimeout(() => {
        transitionTo('video');
        hasShownVideoOnce.current = true;
      }, delay);
    } else {
      // Video mode - wait for video to end or timeout
      timerRef.current = setTimeout(() => {
        transitionTo('visualization');
      }, VIDEO_DURATION_MS);
    }
  }, [currentMode, disableAutoSwap, isActive, clearTimer, transitionTo]);

  // Handle video ending
  const handleVideoEnded = useCallback(() => {
    transitionTo('visualization');
  }, [transitionTo]);

  // Set up automatic transitions
  useEffect(() => {
    scheduleNextTransition();
    return clearTimer;
  }, [scheduleNextTransition, clearTimer]);

  // If user starts interacting, switch back to visualization
  useEffect(() => {
    if ((isActive || disableAutoSwap) && currentMode === 'video') {
      transitionTo('visualization');
    }
  }, [isActive, disableAutoSwap, currentMode, transitionTo]);

  return (
    <div className={cn('relative', className)}>
      {/* CouncilVisualization layer */}
      <div
        className={cn(
          'absolute inset-0 transition-opacity duration-700 ease-in-out',
          currentMode === 'visualization' && !isTransitioning
            ? 'opacity-100 z-10'
            : 'opacity-0 z-0 pointer-events-none'
        )}
      >
        <CouncilVisualization
          className="w-full h-full"
          isActive={isActive}
          phase={phase}
        />
      </div>

      {/* DemoVideoPlayer layer - fills the entire container to match council ring size */}
      <div
        className={cn(
          'absolute inset-0 transition-opacity duration-700 ease-in-out flex items-center justify-center',
          currentMode === 'video' && !isTransitioning
            ? 'opacity-100 z-10'
            : 'opacity-0 z-0 pointer-events-none'
        )}
      >
        <DemoVideoPlayer
          className="w-full h-full max-w-full"
          isVisible={currentMode === 'video'}
          onEnded={handleVideoEnded}
          showControls={true}
        />
      </div>
    </div>
  );
}

